import type { EntityData } from '@adornis/baseql/entities/types.js';
import { alertDialog } from '@adornis/dialog/dialog-functions.js';
import { AdornisFile } from '@adornis/file-utils/db/files.js';
import { CheckboxInput } from '@adornis/formfield/fields/checkbox-input.js';
import type { FunctionalForm } from '@adornis/forms/use-form.js';
import { stateful } from '@adornis/functional-lit/stateful.js';
import { goTo } from '@adornis/router/client/open-href.js';
import { GlobalConfigAPI } from '@martinus/global-config/api/GlobalConfigAPI.js';
import { sgNonOptional } from '@martinus/validation/sgNonOptional.js';
import { html } from 'lit';
import type { Person } from '../../../entities/person.js';

const Declaration = (props: { title: string; body: unknown; checkbox: unknown }) => html`
  <div class="card flex flex-col gap-4 p-6">
    <h3 class="font-semibold">${props.title}</h3>
    ${props.body}
    <div class="pt-2 border-t border-base-300">${props.checkbox}</div>
  </div>
`;

export const EmployeeSectionEinverstaendniserklaerungenForm = stateful(
  (props: { form: FunctionalForm<EntityData<Person>> }) => {
    return html`
      <div class="flex flex-col gap-6">
        ${Declaration({
          title: 'Selbstverpflichtungserklärung',
          body: html`
            <p class="text-base leading-relaxed">
              Die katholische Kirche will in ihren Schulen den Schülerinnen und Schülern Lebensräume bieten, in denen
              sie ihre Persönlichkeit, ihre Fähigkeiten und Begabungen entfalten können. Dies sollen geschützte Orte
              sein, in denen junge Menschen sich angenommen und sicher fühlen. Kinder und Jugendliche brauchen und
              finden Vorbilder, die sie als eigenständige Persönlichkeiten respektieren und unterstützen und denen sie
              vertrauen können. Die Verantwortung für den Schutz der Schülerinnen und Schüler liegt beim Lehrpersonal
              sowie bei den weiteren ehrenamtlichen sowie haupt- und nebenberuflichen Mitarbeitenden der Schule. Diese
              sind zu einem reflektierten Umgang mit ihren Schutzbefohlenen und zur zeitnahen und angemessenen
              Thematisierung von Grenzverletzungen verpflichtet, die durch ihre Kolleginnen und Kollegen oder durch die
              ihnen anvertrauten Schülerinnen und Schüler begangen worden sind. Dies wird durch die Unterzeichnung
              dieser Selbstverpflichtungserklärung bekräftigt.
            </p>
            <p class="text-base leading-relaxed font-medium">
              Ich verpflichte mich, alles in meinen Kräften Stehende zu tun, dass niemand den mir anvertrauten
              Schülerinnen und Schülern seelische, körperliche oder sexualisierte Gewalt antut.
            </p>
            <ol class="flex flex-col gap-3 list-decimal list-outside pl-6">
              <li class="text-base leading-relaxed">
                Ich unterstütze die Schülerinnen und Schüler in ihrer Entwicklung zu eigenverantwortlichen, glaubens-
                und gemeinschaftsfähigen Persönlichkeiten. Ich stärke sie, für ihr Recht auf seelische und körperliche
                Unversehrtheit und ihr Recht auf Hilfe wirksam einzutreten.
              </li>
              <li class="text-base leading-relaxed">
                Meine Arbeit mit den mir anvertrauten Schülerinnen und Schülern ist geprägt von Wertschätzung und
                Vertrauen. Ich achte ihre Rechte und ihre Würde.
              </li>
              <li class="text-base leading-relaxed">
                Ich gehe achtsam und verantwortungsbewusst mit Nähe und Distanz um. Individuelle Grenzen von anderen
                respektiere ich. Dies bezieht sich insbesondere auf die Intimsphäre von jungen Menschen. Ich beachte
                dies auch im Umgang mit Medien, insbesondere bei der Nutzung von Foto, Film, Handy und Internet.
              </li>
              <li class="text-base leading-relaxed">
                Ich beziehe gegen diskriminierendes, gewalttätiges und sexistisches Verhalten, ob in Wort oder Tat,
                aktiv Stellung. Abwertendes Verhalten wird von mir benannt und nicht toleriert.
              </li>
              <li class="text-base leading-relaxed">
                Ich achte auf jede Form persönlicher Grenzverletzung und leite die notwendigen und angemessenen
                Maßnahmen zum Schutz der jungen Menschen ein. Verhalten sich die im Raum der Schule tätigen Personen
                oder die mir anvertrauten Schülerinnen und Schüler sexuell übergriffig oder körperlich gewalttätig,
                setze ich mich für den Schutz der Betroffenen ein. Ich höre zu, wenn sie mir verständlich machen
                möchten, dass ihnen durch weitere Menschen seelische, sexualisierte oder körperliche Gewalt angetan
                wird. Ich bin mir bewusst, dass seelische, sexualisierte und körperliche Gewalt nicht nur von
                männlichen, sondern auch von weiblichen Tätern verübt wird und dass nicht nur Mädchen, sondern auch
                Jungen häufig zu Opfern werden.
              </li>
              <li class="text-base leading-relaxed">
                Im Konfliktfall ziehe ich (professionelle) fachliche Unterstützung und Hilfe hinzu und informiere die
                Verantwortlichen. Die mir anvertrauten Informationen behandele ich sensibel. Der Schutz der Schülerinnen
                und Schüler steht dabei an erster Stelle.
              </li>
              <li class="text-base leading-relaxed">
                Ich bin mir meiner besonderen Vertrauens- und Autoritätsstellung gegenüber den mir anvertrauten
                Schülerinnen und Schülern bewusst und handele nachvollziehbar und ehrlich. Ich nutze keine
                Abhängigkeiten aus.
              </li>
              <li class="text-base leading-relaxed">
                Ich bin mir bewusst, dass jede sexualisierte Handlung mit Schutzbefohlenen gegebenenfalls
                disziplinarische und/oder strafrechtliche Folgen hat.
              </li>
              <li class="text-base leading-relaxed">
                Ich kenne die Verfahrenswege und die entsprechenden (Erst-) Ansprechpartner/innen für mein Bistum, meine
                Schule und deren Träger. Ich weiß, wo ich mich beraten lassen kann oder bei Bedarf Hilfe zur Klärung und
                Unterstützung bekomme und werde sie dann in Anspruch nehmen.
              </li>
              <li class="text-base leading-relaxed">
                Ich wurde zu Fragen des Kinder- und Jugendschutzes unter Berücksichtigung der Vorgaben der Ordnung zur
                Prävention von sexuellem Missbrauch an Minderjährigen meines Bistums durch eine Handreichung informiert,
                habe diese sorgsam gelesen und habe Kenntnis, dass ich mich stets aktuell auf der Bistumshomepage
                <a
                  href="https://www.bistum-mainz.de/praevention"
                  target="_blank"
                  rel="noopener noreferrer"
                  class="link link-primary"
                  >www.bistum-mainz.de/praevention</a
                >
                über Fort- und Weiterbildungsangebote und zu präventionspraktischen Fragestellungen informieren kann.
              </li>
              <li class="text-base leading-relaxed">
                Ich versichere, dass ich nicht wegen einer Straftat im Zusammenhang mit sexualisierter Gewalt
                rechtskräftig verurteilt worden bin und auch insoweit kein Ermittlungsverfahren gegen mich eingeleitet
                worden ist. Für den Fall, dass diesbezüglich ein Ermittlungsverfahren gegen mich eingeleitet wird,
                verpflichte ich mich, dies meinem Dienstvorgesetzten bzw. der Person, die mich zu meiner ehrenamtlichen
                Tätigkeit beauftragt hat, umgehend mitzuteilen.
              </li>
            </ol>
          `,
          checkbox: CheckboxInput({
            ...props.form.field('mitarbeiterDaten', 'anEinverstaendnisErklaerungen', 'selbstverpflichtung'),
            label: 'Erklärung gelesen, verstanden und bestätigt',
          }),
        })}
        ${Declaration({
          title: 'Datenschutzerklärung',
          body: html`
            <p class="text-base leading-relaxed">
              Die Informationen über die Verarbeitung personenbezogener Daten und über meine Rechte nach der
              Datenschutz-Grundverordnung habe ich aus der beiliegenden PDF zur Kenntnis genommen und bin damit
              einverstanden.
            </p>
          `,
          checkbox: CheckboxInput({
            ...props.form.field('mitarbeiterDaten', 'anEinverstaendnisErklaerungen', 'datenschutz'),
            label: 'Erklärung gelesen, verstanden und bestätigt',
          }),
        })}
        ${Declaration({
          title: 'Infektionsschutz',
          body: html`
            <p class="text-base leading-relaxed">
              Von dem Auszug aus dem
              <a
                class="link link-primary"
                @click=${async () => {
                  const config = await GlobalConfigAPI.get()({ infectionInformationPaper: 1 });
                  if (!config.infectionInformationPaper) {
                    await alertDialog(
                      'Das Infektionsschutzpapier ist nicht hinterlegt. Bitte Kontaktieren Sie einen Administrator.',
                    );
                    return;
                  }
                  goTo(AdornisFile.getServeLink(config.infectionInformationPaper), { target: '_blank' });
                }}
                >Gesetz zur Verhütung und Bekämpfung von Infektionskrankheiten beim Menschen</a
              >
              Gesetz zur Verhütung und Bekämpfung von Infektionskrankheiten beim Menschen (Infektionsschutzgesetz –
              IfSG) und den Mitwirkungsverpflichtungen des § 34 IfSG habe ich zur Kenntnis genommen.
            </p>
            <p class="text-base leading-relaxed">Ich werde die Vorschriften beachten.</p>
            <p class="text-base leading-relaxed">
              Das Merkblatt mit dem Gesetztext der §§ 33 bis 35 IfSG wurde mir ausgehändigt.
            </p>
          `,
          checkbox: CheckboxInput({
            ...props.form.field('mitarbeiterDaten', 'anEinverstaendnisErklaerungen', 'infektionsschutz'),
            label: 'Erklärung gelesen, verstanden und bestätigt',
          }),
        })}
        ${Declaration({
          title: 'Sozialversicherungspflicht Student',
          body: html`
            <p class="text-base leading-relaxed">
              Meine Wochenarbeitszeit liegt über 20 Wochenstunden. Eine Beschäftigung als Werkstudent ist daher nicht
              möglich. Mit einer sozialversicherungspflichtigen Beschäftigung bin ich einverstanden.
            </p>
          `,
          checkbox: CheckboxInput({
            ...props.form.field(
              'mitarbeiterDaten',
              'anEinverstaendnisErklaerungen',
              'studentSozialversicherungspflicht',
            ),
            label: 'Erklärung gelesen, verstanden und bestätigt',
          }),
        })}
        ${Declaration({
          title: 'Information für Mitarbeiter',
          body: html`
            <p class="text-base leading-relaxed">Die Informationen für Mitarbeiter habe ich zur Kenntnis genommen.</p>
          `,
          checkbox: CheckboxInput({
            ...props.form.field('mitarbeiterDaten', 'anEinverstaendnisErklaerungen', 'mitarbeiterInformation'),
            label: 'Erklärung gelesen, verstanden und bestätigt',
          }),
        })}
      </div>
    `;
  },
);

export const EmployeeSectionEinverstaendniserklaerungenFormValidator = {
  selbstverpflichtung: sgNonOptional({ allowFalsy: false }),
  datenschutz: sgNonOptional({ allowFalsy: false }),
  infektionsschutz: sgNonOptional({ allowFalsy: false }),
  mitarbeiterInformation: sgNonOptional({ allowFalsy: false }),
};
